import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import { withTranslation, WithTranslation } from 'react-i18next';
import moment from 'moment';
import deburr from 'lodash.deburr';
import isEmpty from 'lodash.isempty';
import { Alert, Select, Tooltip, Popconfirm } from 'antd';
import { PrinterOutlined, CopyOutlined, LinkOutlined, CreditCardOutlined, DownloadOutlined } from '@ant-design/icons';
import { TabLeftPanel, Loader, Icon, BurgerBtn } from 'react-inv-forms';

import {
  openPairEffectsModal,
  openCopySubjectCardModal,
  closeCopySubjectCardModal,
  selectCardToBeCopied,
  setCopyCardsSearch,
  copySubjectCard,
} from '../../../redux/appConfig/actions';
import {
  setFormTemplateVersion,
  toggleEditMode,
  saveFormData,
  updateEditModeStatus,
  saveTemporarilyInSID,
  updateFormData,
  initPublishing,
  getCardFromSid,
} from '../../../redux/dynamicTabs/kartaPrzedmiotu/actions';
import { isTabWithForm } from '../../../redux/dynamicTabs/helpers';
import { UpdateFormDataPayload } from '../../../redux/dynamicTabs/kartaPrzedmiotu/interfaces';
import { closeTab } from '../../../redux/dynamicTabs/actions';
import { RootState } from '../../../redux/reducers';
import withDynamicTabStateProvider, { WithDynamicTabStateProviderProps } from '../../../hoc/withDynamicTabStateProvider';
import withNoAccessToResource from '../../../hoc/withNoAccessToResource';
import withMobileAsideHandler, { WithMobileAsideHandlerProps } from '../../../hoc/withMobileAsideHandler';

import FormContentRenderer from './FormContentRenderer';
import SubjectCardDetailsList from '../../features/SubjectCardDetailsList';
import { translationFromFile } from '../../../helpers/translationFromFile';
import TableCellTypes from '../../../helpers/tableCellTypes';
import DataDestructuring from '../../../helpers/DataDestructuring';
import translationNewLineSeparator from '../../../helpers/translationNewLineSeparator';
import CopyDataFromAnotherCardModal from '../../modals/CopyDataFromAnotherCardModal';

import MobileAside from '../../UI/MobileAside';
import EffectsPairedInfo from '../../UI/EffectsPairedInfo';
import SubjectCardInEdit from '../../UI/SubjectCardInEdit';

import {
  DynamicTabContainer,
  StaticButtonsContainer,
  FormContainer,
  TabRightPanel,
  PrintableBodyWrapper,
  HeaderWrapper,
} from '../../../styles/tabContainers';
import { InlineButton } from '../../../styles/sharedUIElemStyles';
import penWhite from '../../../images/penWhite.svg';
import penGrey from '../../../images/penGrey.svg';
import penRed from '../../../images/penRed.svg';

const { Option } = Select;

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;

type Props = MapStateToProps &
  MapDispatchToProps &
  RouteComponentProps &
  WithMobileAsideHandlerProps &
  WithDynamicTabStateProviderProps<SubjectCardData> &
  DynamicTabComponent &
  WithTranslation;

interface State {
  /** wybrana wersja karty */
  versions: string[];
  /** informacja czy wystąpił błąd */
  error: boolean;
}

/**
 * Komponent zakładki pojedynczej karty przedmiotu (karty-przedmiotów), na którą można wejść z poziomu zakładki Karty Przedmiotów.
 * Zawiera m.in. szczegółowe dane o karcie oraz formularz.
 *
 */
export class SubjectCard extends React.PureComponent<Props, State> {
  private printableRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props as Props);
    const { czyWersja2019, czyWersja2020 } = props.state.leftPanel.data;
    this.state = {
      versions: DataDestructuring.returnAvailableCardVersions(czyWersja2019, czyWersja2020) || [],
      error: false,
    };
  }

  componentDidCatch = (e: any): void => {
    this.setState((state: State) => ({ ...state, error: true }));
  };

  componentDidMount(): void {
    const { przedmiotId } = this.props.state.leftPanel.data;
    if (przedmiotId) {
      this.setTemplateVersion();
    }
  }

  componentDidUpdate(prevProps: Props): void {
    const { przedmiotId } = this.props.state.leftPanel.data;
    if (przedmiotId && !this.state.versions.length && !prevProps.state.leftPanel.data.przedmiotId) {
      this.setTemplateVersion();
    }
  }

  setTemplateVersion = () => {
    const { czyWersja2019, czyWersja2020 } = this.props.state.leftPanel.data;
    const versions: string[] = DataDestructuring.returnAvailableCardVersions(czyWersja2019, czyWersja2020);
    this.setState((state: State) => ({ ...state, versions }));
    if (versions.length) {
      this.props.setFormTemplateVersion(versions[0]);
    } else {
      this.props.closeTab(Number(this.props.id), this.props.history);
    }
  };

  selectFormVersion = (value: string): void => {
    this.props.setFormTemplateVersion(value);
  };

  openPairEffects = (): void => {
    const { przedmiotId, przedmiotNazwa, jezykKartyLista, przedmiotKod } = this.props.state.leftPanel.data;
    const cardData = {
      przedmiotId,
      przedmiotNazwa,
      jezykKartyLista,
      przedmiotKod,
      wersjaKartyLista: this.state.versions,
    };
    this.props.openPairEffectsModal(cardData);
  };

  generateLeftPanelDOM = () => {
    const { loading, data } = this.props.state.leftPanel;
    return (
      <TabLeftPanel>
        <>
          {loading ? (
            <Loader
              style={{
                position: 'absolute',
                left: '40%',
                top: '10%',
                fontSize: '32px',
              }}
            />
          ) : Object.entries(data).length ? (
            <SubjectCardDetailsList data={TableCellTypes.generateTableContent([data], this.props.columns)[0]} />
          ) : (
            <span className='block-centered'>{this.props.t('NO_DATA')}</span>
          )}
        </>
      </TabLeftPanel>
    );
  };

  render(): React.ReactElement {
    if (isTabWithForm(this.props.state)) {
      const { leftPanel, form } = this.props.state;
      const { templateVersion, readOnly, formValues } = form;
      const loadingForms = form.loadingTemplate || form.loadingValues;
      const formyZajecIsEmpty = isEmpty(formValues.find(value => value.dataIndex === 'formyZajecLiczbaGodzin')?.data);
      const buttonPanelDisabled = !readOnly || !this.state.versions.length || !templateVersion || loadingForms;
      if (this.state.error) return <Alert message={this.props.t('UNKNOWED_ERROR_TITLE')} type='error' />;
      return (
        <DynamicTabContainer>
          {this.props.showAsideButton && (
            <MobileAside opened={this.props.openedMobileAside}>{this.generateLeftPanelDOM()}</MobileAside>
          )}
          {this.props.showAsideButton ? null : this.generateLeftPanelDOM()}
          <TabRightPanel withFullWidth={this.props.showAsideButton}>
            {this.props.showAsideButton && <BurgerBtn onClick={this.props.toggleAside} opened={this.props.openedMobileAside} />}
            <StaticButtonsContainer paddingBottom={20} paddingTop={20}>
              <ReactToPrint
                documentTitle={deburr(
                  `${leftPanel.data?.przedmiotNazwa}_${leftPanel.data?.przedmiotKod}_${translationFromFile(
                    'SUBJECT_CARD_PRINT_HEADER',
                  )}_${moment().format('DD-MM-YYYY')}`.replaceAll(' ', '-'),
                )}
                trigger={() => (
                  <InlineButton outlined disabled={buttonPanelDisabled} shape='circle' data-cy='btnPrintCard'>
                    <PrinterOutlined />
                    {this.props.t('BUTTON_PRINT_CARD')}
                  </InlineButton>
                )}
                content={() => this.printableRef.current}
              />
              <Tooltip title={form.isPossibleSaveInSID?.status && !loadingForms ? null : form.isPossibleSaveInSID?.message}>
                <span>
                  <InlineButton
                    onClick={this.props.openCopySubjectCardModal.bind(null, leftPanel.data.przedmiotId)}
                    outlined={true}
                    disabled={buttonPanelDisabled || !form.isPossibleSaveInSID?.status}
                    data-cy='btnCopyDataFromOtherCard'
                  >
                    <CopyOutlined />
                    {this.props.t('BUTTON_COPY_DATA_FROM_OTHER_CARD')}
                  </InlineButton>
                </span>
              </Tooltip>

              <Tooltip title={form.isPossibleSaveInSID?.status && !loadingForms ? null : form.isPossibleSaveInSID?.message}>
                <span>
                  <InlineButton
                    onClick={this.openPairEffects}
                    disabled={buttonPanelDisabled || !form.isPossibleSaveInSID?.status}
                    outlined={true}
                    data-cy='btnPairEffects'
                  >
                    <LinkOutlined />
                    {this.props.t('BUTTON_PAIR_EFFECTS')}
                  </InlineButton>
                </span>
              </Tooltip>
              <Popconfirm
                id='btnGetCardFromSidPopconfirm'
                title={translationNewLineSeparator(
                  this.props.t(
                    form.inEdition
                      ? 'BUTTON_GET_CARD_FROM_SID_POPCONFIRM_IN_EDIT'
                      : 'BUTTON_GET_CARD_FROM_SID_POPCONFIRM_NOT_IN_EDIT',
                  ),
                )}
                okText={this.props.t('YES')}
                cancelText={this.props.t('NO')}
                overlayClassName='custom_popconfirm'
                onConfirm={this.props.getCardFromSid}
              >
                <InlineButton disabled={buttonPanelDisabled} outlined={true} data-cy='btnGetCardFromSid'>
                  <DownloadOutlined />
                  {this.props.t('BUTTON_GET_CARD_FROM_SID')}
                </InlineButton>
              </Popconfirm>
              <Tooltip title={form.isPossibleSaveInSID?.status && !loadingForms ? null : form.isPossibleSaveInSID?.message}>
                <span>
                  <Popconfirm
                    id='btnSaveCardInSidPopconfirm'
                    title={translationNewLineSeparator(this.props.t('BUTTON_SAVE_CARD_IN_SID_POPCONFIRM'))}
                    okText={this.props.t('YES')}
                    cancelText={this.props.t('NO')}
                    overlayClassName='custom_popconfirm'
                    onConfirm={this.props.initPublishing}
                  >
                    <InlineButton
                      disabled={buttonPanelDisabled || !form.isPossibleSaveInSID?.status}
                      outlined={true}
                      data-cy='btnSaveCardInSID'
                    >
                      <CreditCardOutlined />
                      {this.props.t('BUTTON_SAVE_CARD_IN_SID')}
                    </InlineButton>
                  </Popconfirm>
                </span>
              </Tooltip>
              {!readOnly ? (
                <>
                  <InlineButton
                    loading={loadingForms}
                    disabled={loadingForms || !this.state.versions.length}
                    onClick={this.props.saveFormData}
                    data-cy='btnSaveCard'
                  >
                    <Icon path={loadingForms || !this.state.versions.length ? penGrey : penWhite} />
                    {this.props.t('BUTTON_SAVE')}
                  </InlineButton>
                  <InlineButton onClick={this.props.toggleEditMode} outlined={true} data-cy='btnCancelEditCard'>
                    <Icon path={penRed} />
                    {this.props.t('CANCEL')}
                  </InlineButton>
                </>
              ) : (
                <Tooltip
                  title={
                    form.isPossibleSaveInSID?.status && !loadingForms
                      ? formyZajecIsEmpty && !loadingForms
                        ? this.props.t('KP_FORMY_ZAJEC_EMPTY')
                        : null
                      : form.isPossibleSaveInSID?.message
                  }
                >
                  <span>
                    <InlineButton
                      disabled={
                        loadingForms || !this.state.versions.length || formyZajecIsEmpty || !form.isPossibleSaveInSID?.status
                      }
                      onClick={this.props.updateEditModeStatus}
                      data-cy='btnEditCard'
                    >
                      <Icon
                        path={
                          loadingForms || !this.state.versions.length || formyZajecIsEmpty || !form.isPossibleSaveInSID?.status
                            ? penGrey
                            : penWhite
                        }
                      />
                      {this.props.t('BUTTON_EDIT_ON')}
                    </InlineButton>
                  </span>
                </Tooltip>
              )}
            </StaticButtonsContainer>
            {!this.state.versions.length ? (
              leftPanel.loading || loadingForms ? null : (
                <FormContainer>
                  <span className='centered'>{this.props.t('SUBJECT_CARD_NO_AVAILABLE_VERSIONS')}</span>
                </FormContainer>
              )
            ) : (
              <FormContainer overflow>
                <SubjectCardInEdit inEdition={form.inEdition} />
                <EffectsPairedInfo areEffectsPaired={leftPanel.data.czySparowanoEfekty === 'tak'} />
                <h2>{this.props.t('SELECT_FORM_VERSION')}</h2>
                <Select
                  loading={loadingForms}
                  disabled={loadingForms || !readOnly}
                  placeholder={this.props.t('SELECT_FORM_VERSION')}
                  onSelect={this.selectFormVersion}
                  notFoundContent={this.props.t('NO_DATA')}
                  dropdownClassName='custom_dropdown'
                  className='custom_select'
                  value={templateVersion ? templateVersion : undefined}
                >
                  {this.state.versions.map((lang: string) => (
                    <Option key={lang} value={lang} className='custom_option'>
                      {`${lang}`}
                    </Option>
                  ))}
                </Select>
                <FormContentRenderer updateFormData={this.props.updateFormData} forms={form} />
              </FormContainer>
            )}
          </TabRightPanel>
          <PrintableBodyWrapper>
            <div ref={this.printableRef}>
              <HeaderWrapper smaller={true}>
                <p>{this.props.t('SUBJECT_CARD_PRINT_HEADER')}</p>
                <span>
                  {leftPanel.data?.przedmiotNazwa} ({leftPanel.data?.przedmiotKod})
                </span>
                <span>
                  {this.props.t('WERSJA')} {templateVersion}
                </span>
              </HeaderWrapper>
              <FormContentRenderer updateFormData={this.props.updateFormData} forms={form} toPrint={true} />
            </div>
          </PrintableBodyWrapper>
          {/* modals */}
          {this.props.copyCardsDataModal.opened ? (
            <CopyDataFromAnotherCardModal
              close={this.props.closeCopySubjectCardModal}
              cardList={this.props.copyCardsDataModal.cardsList}
              opened={this.props.copyCardsDataModal.opened}
              loading={this.props.copyCardsDataModal.loading}
              selected={this.props.copyCardsDataModal.selected}
              onOk={this.props.copySubjectCard}
              select={this.props.selectCardToBeCopied}
              setSearchCopyCardsData={this.props.setCopyCardsSearch}
            />
          ) : null}
        </DynamicTabContainer>
      );
    } else return <></>;
  }
}

const mapStateToProps = (state: RootState) => ({
  columns: state.StaticViews.SubjectsCards.columns,
  copyCardsDataModal: state.AppConfig.copyCardsDataModal,
});

const mapDispatchToProps = (dispatch: any, { id }: DynamicTabComponent) => ({
  openPairEffectsModal: (subjectCardData: Modal.PairEffectsData) => dispatch(openPairEffectsModal(subjectCardData, id)),
  toggleEditMode: () => dispatch(toggleEditMode(id)),
  saveFormData: () => dispatch(saveFormData(id, true)),
  getCardFromSid: () => dispatch(getCardFromSid(id, true)),
  updateFormData: (payload: UpdateFormDataPayload) => dispatch(updateFormData(id, payload)),
  setFormTemplateVersion: (wersjaSzablonuKarty: string) => dispatch(setFormTemplateVersion(id, wersjaSzablonuKarty)),
  openCopySubjectCardModal: (przedmiotId: Przedmiot['przedmiotId']) => dispatch(openCopySubjectCardModal(przedmiotId, id)),
  closeCopySubjectCardModal: () => dispatch(closeCopySubjectCardModal()),
  selectCardToBeCopied: (id: SubjectCardData['przedmiotId']): void => dispatch(selectCardToBeCopied(id)),
  setCopyCardsSearch: (search: string) => dispatch(setCopyCardsSearch(search)),
  copySubjectCard: () => dispatch(copySubjectCard()),
  closeTab: (index: number, history: any) => dispatch(closeTab(index, history)),
  saveTemporarilyInSID: () => dispatch(saveTemporarilyInSID(id)),
  initPublishing: () => dispatch(initPublishing(id)),
  updateEditModeStatus: () => dispatch(updateEditModeStatus(id)),
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withMobileAsideHandler,
  withDynamicTabStateProvider,
  withNoAccessToResource,
)(SubjectCard);
